'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { getInviteByCode, acceptInvite } from '@/services/inviteService';
import InviteProGuard from '@/components/invite/InviteProGuard';
import LegalLinks from '@/components/LegalLinks';
import Confetti from 'react-confetti';
import {
  CheckCircleIcon,
  UsersIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function audienceLabel(audience, role, t) {
  const a = String(audience || '').toLowerCase();
  if (a === 'parent') return t('acceptInvite.audience.parent');
  if (a === 'player') return t('acceptInvite.audience.player');
  return role || t('acceptInvite.audience.member');
}

/**
 * Where to send the user after a successful invite-accept.
 * Parents land on the post-invite welcome wizard (phone verify + push
 * opt-in) the first time. The wizard self-skips on subsequent accepts
 * by checking users/{uid}.parentOnboardingComplete and bouncing to
 * /parent/teams if it's already set. Coaches go straight to /teams.
 */
function postAcceptRoute(audience) {
  return String(audience || '').toLowerCase() === 'parent' ? '/parent/welcome' : '/teams';
}

export default function AcceptInvite() {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [accepting, setAccepting] = useState(false);
  // Team logo URL (fetched separately — invites only carry teamName for
  // the unauthenticated landing page, not the logo). Public team-doc
  // reads are allowed by Firestore rules so this is safe pre-auth.
  const [teamLogoUrl, setTeamLogoUrl] = useState(null);
  // Confetti control — only fires once per page load, parent invites only.
  // Auto-stops after a few seconds so it isn't annoying.
  const [showConfetti, setShowConfetti] = useState(false);
  const [viewport, setViewport] = useState({
    width:  typeof window !== 'undefined' ? window.innerWidth  : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Track viewport so confetti fills the screen even on rotate / resize.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch the team's logo for the celebratory hero. Public team-doc reads are
  // allowed by Firestore rules so this works pre-auth — important because the
  // user may not be signed in yet when they tap the invite link.
  useEffect(() => {
    let cancelled = false;
    if (!invite?.teamId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'teams', invite.teamId));
        if (cancelled) return;
        if (snap.exists()) {
          const t = snap.data() || {};
          // Try a few common field names so this is forgiving across older
          // team docs that used different conventions.
          const logo = t.logoUrl || t.logo || t.crestUrl || t.imageUrl || null;
          if (logo) setTeamLogoUrl(logo);
        }
      } catch (err) {
        // Logo is purely decorative — never block the invite flow on this.
        console.warn('[AcceptInvite] team logo fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [invite?.teamId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!code) {
          toast.error(t('acceptInvite.toast.invalidUrl'));
          setLoading(false);
          return;
        }
        const data = await getInviteByCode(code);
        if (cancelled) return;
        if (!data) {
          toast.error(t('acceptInvite.toast.notFound'));
        } else {
          setInvite(data);
          // Render this landing page in the family's language — the same
          // one stamped on the invite (and used for the invite email),
          // rather than defaulting to the visitor's browser language. So
          // a Spanish-speaking family sees a Spanish page even on an
          // English device. No localStorage write — we don't clobber a
          // signed-in user's saved preference, just this render.
          const raw = String(data.language || '').toLowerCase();
          if (raw) {
            const inviteLang = raw.startsWith('es') ? 'es' : 'en';
            if (i18n.language !== inviteLang) i18n.changeLanguage(inviteLang);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, t, i18n]);

  const isParent = useMemo(
    () => String(invite?.audience || '').toLowerCase() === 'parent',
    [invite]
  );

  // Pop confetti once when a parent invite first lands. Auto-stops after a
  // few seconds — the goal is a delight moment, not a permanent distraction.
  // Coaches/staff get the existing business-tone landing (no confetti).
  useEffect(() => {
    if (!invite || invite.status !== 'active') return;
    if (!isParent) return;
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 6000);
    return () => clearTimeout(t);
  }, [invite, isParent]);

  // Coach/staff may have stamped a player name onto the invite metadata so
  // we can personalize the parent landing ("Jordan has been invited..."). All
  // optional — gracefully fall back to a generic warm message if absent.
  const playerName =
    invite?.metadata?.playerName ||
    invite?.metadata?.childName ||
    invite?.playerName ||
    null;

  // Email-mismatch detection. The invite was sent to invite.email,
  // but the user might be signed in with a different account
  // (shared family device, multiple Google sessions, etc.). Without
  // surfacing this, a coach who's logged in as their coach account
  // and clicks a parent invite they sent themselves ends up with
  // the parent role bound to their COACH account — confusing. We
  // warn here but still allow override (legit cases exist: the
  // coach IS the parent of their own kid).
  const inviteEmailLower = String(invite?.email || '').trim().toLowerCase();
  const userEmailLower = String(user?.email || '').trim().toLowerCase();
  const emailMismatch = !!(
    user?.uid &&
    inviteEmailLower &&
    userEmailLower &&
    inviteEmailLower !== userEmailLower
  );

  // "Switch account" path: sign the user out, then send them to
  // /login with the invite code preserved so once they sign in with
  // the right email this same page picks back up.
  //
  // Note: this calls auth.logout directly (not the useLogout hook)
  // because we need to navigate to /login with custom query params
  // (invite code + teamId), and useLogout always navigates to bare
  // /login. To stay consistent with the cleanup useLogout does, we
  // mirror its sessionStorage clear here so per-session UI flags
  // (dashboardSeen, postLoginRedirect) don't leak into the next
  // user's session in the same tab. Without this, a parent on a
  // shared family device who switches accounts can land on stale
  // bypass behavior or a wrong post-login redirect.
  const handleSwitchAccount = async () => {
    try { sessionStorage.setItem('pendingInviteCode', code); } catch {}
    try { await logout(); } catch (err) { console.warn('[AcceptInvite] logout failed', err); }
    try {
      sessionStorage.removeItem('scrimly.dashboardSeen');
      sessionStorage.removeItem('postLoginRedirect');
    } catch {}
    const params = new URLSearchParams({ invite: code });
    if (invite?.teamId) params.set('t', invite.teamId);
    navigate(`/login?${params.toString()}`);
  };

  // Build the auth-redirect URL with the invite code preserved so the
  // post-auth flow brings the user back to /invite/{code} to accept.
  // Shared by both the primary "Sign up/Sign in to join" button AND the
  // "Already have an account? Sign in" secondary link rendered below
  // (parent invites only).
  const authRedirectUrl = (target) => {
    const params = new URLSearchParams({ invite: code });
    if (invite?.teamId) params.set('t', invite.teamId);
    return `${target}?${params.toString()}`;
  };

  const goToAuth = (target) => {
    try { sessionStorage.setItem('pendingInviteCode', code); } catch {}
    navigate(authRedirectUrl(target));
  };

  const onAccept = async () => {
    if (accepting) return;
    if (!user?.uid) {
      // Parents default to /register (most common case — first-time invitee
      // creating an account). The "I already have an account → Sign in"
      // secondary link below covers parents whose email is already in our
      // system (re-invited after roster delete, or a coach who's also the
      // parent of their own kid). Coaches always go to /login.
      goToAuth(isParent ? '/register' : '/login');
      return;
    }
    setAccepting(true);
    try {
      await acceptInvite({ code });
      toast.success(t('acceptInvite.toast.welcome'));
      navigate(postAcceptRoute(invite?.audience));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t('acceptInvite.toast.acceptFailed'));
      setAccepting(false);
    }
  };

  const Shell = ({ children }) => (
    <div
      className="relative min-h-screen overflow-hidden bg-[#0d1f14] text-white"
      style={{ fontFamily: "'Barlow', sans-serif" }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="pointer-events-none absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-[#b5f03d]/10 blur-3xl" />
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col items-center justify-center p-6">
        <div className="w-full">{children}</div>
        {/* Legal footer */}
        <div className="mt-6 w-full">
          <LegalLinks variant="subtle" className="!text-white/40" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="rounded-2xl border border-white/10 bg-[#162b1e] p-8">
          <div className="h-6 w-40 rounded bg-white/10 animate-pulse mb-3" />
          <div className="h-4 w-full rounded bg-white/5 animate-pulse mb-2" />
          <div className="h-4 w-3/4 rounded bg-white/5 animate-pulse" />
        </div>
      </Shell>
    );
  }

  if (!invite || invite.status !== 'active') {
    return (
      <Shell>
        <div className="rounded-2xl border border-[#b5f03d]/30 bg-[#162b1e] p-8 text-center">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#b5f03d]" />
          </div>
          <h1
            className="uppercase text-white"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 26 }}
          >
            {t('acceptInvite.invalid.title')}
          </h1>
          <p className="mt-3 text-white/70 text-[15px]">
            {t('acceptInvite.invalid.body1')}
          </p>
          <p className="mt-4 text-white/60 text-[14px]">
            {t('acceptInvite.invalid.body2')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[52px] uppercase transition-colors"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16, letterSpacing: '0.6px' }}
          >
            {t('acceptInvite.invalid.cta')}
            <ArrowRightIcon className="h-4 w-4" />
          </button>
          <p className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-white/40">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            {t('acceptInvite.securedBy')}
          </p>
        </div>
      </Shell>
    );
  }

  const Inner = (
    <Shell>
      {/* Confetti — parent invites only, fires once on landing.
          Pinned to viewport so it rains over the whole screen, not just the card. */}
      {isParent && showConfetti && (
        <div className="pointer-events-none fixed inset-0 z-50">
          <Confetti
            width={viewport.width}
            height={viewport.height}
            numberOfPieces={220}
            recycle={false}
            gravity={0.18}
            colors={['#b5f03d', '#94cc28', '#ffffff', '#7dd3fc', '#fde68a']}
          />
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-[#162b1e] p-7 sm:p-8">
        {isParent ? (
          // ── Parent landing: celebratory hero ─────────────────────────────
          <div className="text-center">
            {/* Team crest if we have one, otherwise a lime sparkle medallion */}
            {teamLogoUrl ? (
              <div className="mx-auto mb-5 h-24 w-24 rounded-full ring-4 ring-[#b5f03d]/40 ring-offset-4 ring-offset-[#162b1e] overflow-hidden bg-white/5 flex items-center justify-center">
                <img
                  src={teamLogoUrl}
                  alt={invite.teamName ? t('acceptInvite.parent.crestAlt', { team: invite.teamName }) : t('acceptInvite.parent.crestAltGeneric')}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-[#b5f03d]/15 ring-4 ring-[#b5f03d]/30 ring-offset-4 ring-offset-[#162b1e]">
                <SparklesIcon className="h-10 w-10 text-[#b5f03d]" />
              </div>
            )}

            <p
              className="text-[12px] font-bold uppercase tracking-[1.6px] text-[#b5f03d] mb-2"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('acceptInvite.parent.eyebrow')}
            </p>
            <h1
              className="uppercase text-white leading-[1.02]"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 30 }}
            >
              {playerName
                ? t('acceptInvite.parent.titleNamed', { name: playerName })
                : t('acceptInvite.parent.titleGeneric')}
            </h1>
            {invite.teamName && (
              <p
                className="mt-2 text-white/80 text-[15px]"
                style={{ fontFamily: HEAD_FONT, fontWeight: 600, letterSpacing: '0.4px' }}
              >
                {t('acceptInvite.parent.toJoin')} <span className="text-[#b5f03d]">{invite.teamName}</span>
              </p>
            )}

            <p className="mt-4 text-white/70 text-[14.5px] leading-relaxed">
              {t('acceptInvite.parent.intro')}
            </p>

            <div className="mt-6 space-y-3 text-left">
              {[
                t('acceptInvite.parent.bullet1'),
                t('acceptInvite.parent.bullet2'),
                t('acceptInvite.parent.bullet3'),
              ].map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <CheckCircleIcon className="h-5 w-5 mt-0.5 text-[#b5f03d] shrink-0" />
                  <span className="text-[14px] text-white/85 leading-snug">{b}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // ── Coach / staff landing: existing business-tone layout ─────────
          <>
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
              <UsersIcon className="h-6 w-6 text-[#b5f03d]" />
            </div>

            <p
              className="text-[12px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-2"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('acceptInvite.coach.eyebrow')}
            </p>
            <h1
              className="uppercase text-white leading-[1.05]"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 32 }}
            >
              {invite.teamName ? t('acceptInvite.coach.titleNamed', { team: invite.teamName }) : t('acceptInvite.coach.titleGeneric')}
            </h1>

            <p className="mt-4 text-white/70 text-[15px] leading-relaxed">
              {t('acceptInvite.coach.body', { label: audienceLabel(invite.audience, invite.role, t) })}
            </p>
          </>
        )}

        {/* Email mismatch warning. Surfaces when the signed-in user's
            email differs from the invite's email — a common footgun
            on shared devices or when a coach clicks a parent invite
            they sent themselves while still signed in as the coach.
            We warn but don't hard-block: legitimate overlap exists
            (a coach who's also a parent of their own kid). */}
        {emailMismatch && (
          <div className="mt-8 rounded-xl border border-amber-400/40 bg-amber-500/10 p-4">
            <p
              className="text-[12px] font-bold uppercase tracking-[1.2px] text-amber-200 mb-1.5"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {t('acceptInvite.mismatch.title')}
            </p>
            <p className="text-[13.5px] text-amber-100/85 leading-relaxed">
              {t('acceptInvite.mismatch.bodyPrefix')}
              <strong className="text-amber-100">{invite.email}</strong>
              {t('acceptInvite.mismatch.bodyMid')}
              <strong className="text-amber-100">{user.email}</strong>
              {t('acceptInvite.mismatch.bodySuffix')}
            </p>
            <button
              type="button"
              onClick={handleSwitchAccount}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-400/40 bg-amber-500/15 hover:bg-amber-500/25 px-3 py-2 text-[12px] font-bold uppercase tracking-[0.6px] text-amber-100 transition-colors"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('acceptInvite.mismatch.switch')}
            </button>
          </div>
        )}

        <div className="mt-8 space-y-2">
          <button
            type="button"
            onClick={onAccept}
            disabled={accepting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[52px] uppercase transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16, letterSpacing: '0.6px' }}
          >
            {accepting
              ? t('acceptInvite.accept.joining')
              : !user?.uid
                ? (isParent ? t('acceptInvite.accept.signUpToJoin') : t('acceptInvite.accept.signInToJoin'))
                : emailMismatch
                  ? t('acceptInvite.accept.acceptAnyway', { email: user.email })
                  : t('acceptInvite.accept.acceptInvite')}
            <ArrowRightIcon className="h-4 w-4" />
          </button>

          {/* "Already have an account? Sign in" — parent invites only,
              and only when the user isn't signed in yet. Coaches already
              use /login as their primary path so they don't need the extra
              affordance. This covers the re-invite-after-roster-delete
              footgun: a parent whose email was previously seeded as a
              registration ends up in Firebase Auth, and tapping "Sign up
              to join" hits an "email already in use" wall with no path
              forward. */}
          {isParent && !user?.uid && (
            <button
              type="button"
              onClick={() => goToAuth('/login')}
              className="inline-flex w-full items-center justify-center rounded-[10px] py-3 text-[13px] text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              {t('acceptInvite.haveAccount')}&nbsp;
              <span className="font-bold underline underline-offset-2 text-[#b5f03d]">
                {t('acceptInvite.signIn')}
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(user?.uid ? '/' : '/login')}
            className="inline-flex w-full items-center justify-center rounded-[10px] py-3 text-[13px] font-medium text-white/55 hover:text-white/85 hover:bg-white/[0.04] transition-colors"
          >
            {t('acceptInvite.cancel')}
          </button>
        </div>

        <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-center gap-1.5 text-[11px] text-white/40">
          <ShieldCheckIcon className="h-3.5 w-3.5" />
          {t('acceptInvite.securedBy')}
        </div>
      </div>
    </Shell>
  );

  // The Pro-tier limit is a *staff*-side concept (managing multiple teams).
  // Parent invites are read-only memberships and shouldn't trigger the upsell.
  if (isParent) return Inner;
  return <InviteProGuard>{Inner}</InviteProGuard>;
}
